import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/authContext";
import { endpoints, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Info } from "lucide-react";
import { postAuthDestination } from "@/lib/postAuthRouting";

const GOOGLE_AUTH_URL = "https://accounts.google.com/o/oauth2/v2/auth";
const BUILD_TIME_GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

const OAUTH_MESSAGES = {
    cancelled: "Google sign-in was cancelled. You can try again or sign in another way.",
    denied: "Google sign-in was cancelled. You can try again or sign in another way.",
    expired: "That Google sign-in session expired. Please try again.",
    invalid_state: "Google sign-in couldn't be verified. Please try again.",
    provider_error: "Google is having trouble right now — try again or use email.",
    link_error: "We couldn't link that Google account. Try signing in with your email.",
    failed: "We couldn't complete Google sign-in. Try again or use email.",
    unconfigured: "Google sign-in is temporarily unavailable. Please sign in with your email.",
};

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function customGoogleRedirect(next, clientId) {
    const state = window.crypto?.randomUUID
        ? window.crypto.randomUUID().replace(/-/g, "")
        : Math.random().toString(36).slice(2) + Date.now().toString(36);
    try {
        if (next) sessionStorage.setItem("pm_oauth_next", next);
        else sessionStorage.removeItem("pm_oauth_next");
        sessionStorage.setItem("pm_oauth_state", state);
    } catch {}
    const redirect = window.location.origin + "/auth/google";
    const url = `${GOOGLE_AUTH_URL}?client_id=${encodeURIComponent(clientId)}`
        + `&redirect_uri=${encodeURIComponent(redirect)}`
        + `&response_type=code`
        + `&scope=${encodeURIComponent("openid email profile")}`
        + `&state=${encodeURIComponent(state)}`
        + `&prompt=select_account`
        + `&access_type=online`
        + `&include_granted_scopes=true`;
    window.location.href = url;
}

export default function SignIn() {
    const nav = useNavigate();
    const [params] = useSearchParams();
    const next = params.get("next");
    const oauth = params.get("oauth");
    const { login, user } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");
    const [googleEnabled, setGoogleEnabled] = useState(false);
    const [googleClientId, setGoogleClientId] = useState("");

    // Routing decision consults server-side onboarding_state + memberships,
    // not the legacy global user.role, so an incomplete Artist onboarding
    // always resumes on next login.
    const resolveDest = async () => postAuthDestination({ explicitNext: next || "" });

    useEffect(() => {
        if (user && typeof user === "object") {
            (async () => {
                const d = await resolveDest();
                nav(d, { replace: true });
            })();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, next]);

    useEffect(() => {
        if (oauth && OAUTH_MESSAGES[oauth]) setNotice(OAUTH_MESSAGES[oauth]);
    }, [oauth]);

    useEffect(() => {
        // Backend is source of truth: if it returns enabled + a client_id, we
        // activate the custom Project-Mint-branded Google flow — regardless of
        // whether REACT_APP_GOOGLE_CLIENT_ID was baked into this frontend build.
        endpoints.authGoogleConfig()
            .then((d) => {
                const cid = (d && d.client_id) || BUILD_TIME_GOOGLE_CLIENT_ID;
                if (d && d.enabled && cid) {
                    setGoogleClientId(cid);
                    setGoogleEnabled(true);
                } else {
                    setGoogleEnabled(false);
                }
            })
            .catch(() => setGoogleEnabled(false));
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        try {
            await login(email, password);
            toast.success("Welcome back");
            const d = await resolveDest();
            nav(d, { replace: true });
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const onGoogle = () => {
        if (googleEnabled && googleClientId) {
            customGoogleRedirect(next, googleClientId);
        } else {
            setNotice(OAUTH_MESSAGES.unconfigured);
        }
    };

    return (
        <div className="max-w-md mx-auto px-4 md:px-6 py-16" data-testid="signin-page">
            <div className="border border-[#2a2a2a] p-8">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#ff4400]">Project Mint</div>
                <h1 className="mt-2 font-display font-black uppercase tracking-tighter text-4xl">Sign in</h1>

                {notice && (
                    <div className="mt-6 border border-[#2a2a2a] bg-[#141414] p-3 flex gap-2" data-testid="signin-oauth-notice">
                        <Info className="w-4 h-4 text-[#ff9500] mt-0.5 flex-shrink-0" />
                        <div className="text-xs text-[#a3a3a3] leading-relaxed">{notice}</div>
                    </div>
                )}

                <button
                    type="button"
                    onClick={onGoogle}
                    disabled={!googleEnabled}
                    className="pm-btn w-full mt-6 flex items-center justify-center gap-2 border-[#f4f4f0] disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="signin-google-btn"
                    data-google-flow={googleEnabled ? "custom" : "disabled"}
                >
                    <GoogleGlyph /> Continue with Google
                </button>

                <div className="mt-6 flex items-center gap-3 text-[10px] font-mono uppercase text-[#666]">
                    <div className="flex-1 h-px bg-[#2a2a2a]" /> or email <div className="flex-1 h-px bg-[#2a2a2a]" />
                </div>

                <form onSubmit={submit} className="mt-6 space-y-4">
                    <Field label="Email" value={email} onChange={setEmail} type="email" autoComplete="email" testId="signin-email" />
                    <Field label="Password" value={password} onChange={setPassword} type="password" autoComplete="current-password" testId="signin-password" />
                    {error && <div className="text-xs font-mono uppercase text-[#ff3333]" data-testid="signin-error">{error}</div>}
                    <button
                        type="submit"
                        disabled={busy || !email || !password}
                        className="pm-btn pm-btn-primary w-full mt-2"
                        data-testid="signin-submit"
                    >
                        {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        Sign in
                    </button>
                </form>

                <div className="mt-6 flex items-center justify-between text-xs font-mono uppercase">
                    <Link to="/forgot" className="text-[#a3a3a3] hover:text-[#ff4400]" data-testid="signin-forgot-link">Forgot password?</Link>
                    <Link to={`/signup${params.get("next") ? `?next=${encodeURIComponent(next)}` : ""}`} className="text-[#ff4400] hover:underline" data-testid="signin-signup-link">Create account →</Link>
                </div>
            </div>
        </div>
    );
}

function Field({ label, value, onChange, type = "text", testId, autoComplete }) {
    return (
        <label className="block">
            <span className="text-[10px] font-mono uppercase tracking-widest text-[#a3a3a3]">{label}</span>
            <input
                type={type}
                autoComplete={autoComplete}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full mt-1 bg-transparent border-b border-[#2a2a2a] focus:border-[#ff4400] py-2 outline-none text-[#f4f4f0]"
                data-testid={testId}
            />
        </label>
    );
}

function GoogleGlyph() {
    return (
        <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden>
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
        </svg>
    );
}
